import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip, Tag, Input, Button, Space, DatePicker } from 'antd'
import { SearchOutlined, CalendarOutlined } from '@ant-design/icons'
import { LegalNoticeIndexTableType } from './types'
import { ColumnType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'

const { RangePicker } = DatePicker

export const LES_ECHOS_STATUSES = [
  'review',
  'refused',
  'pending',
  'canceled',
  'validated',
  'published',
]

export const INTERNAL_STATUSES = [
  'editing',
  'pending',
  'submitted',
  'published',
  'canceled',
]

const STATUS_COLORS: Record<string, string> = {
  published: 'green',
  validated: 'cyan',
  pending: 'blue',
  review: 'orange',
  submitted: 'geekblue',
  editing: 'default',
  refused: 'red',
  canceled: 'volcano',
}

export const statusFilterOptions = (providerKey: string) => {
  const list =
    providerKey === 'internal' ? INTERNAL_STATUSES : LES_ECHOS_STATUSES
  return list.map(s => ({ text: s, value: s }))
}

export const getStatusColumnProps = (
  variables: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  providerKey: string
): ColumnType<LegalNoticeIndexTableType> => ({
  filtered: !!variables.filters?.status,
  filteredValue: variables.filters?.status ?? null,
  filters: statusFilterOptions(providerKey),
})

export const getProviderColumnProps = (
  variables: any // eslint-disable-line @typescript-eslint/no-explicit-any
): ColumnType<LegalNoticeIndexTableType> => ({
  filtered: !!variables.filters?.provider,
  filteredValue: variables.filters?.provider ?? null,
  filters: [{ text: 'Les Echos', value: 'les_echos' }],
})

const renderTooltip = (text: string | null | number) =>
  text != null ? (
    <Tooltip placement="topLeft" title={String(text)}>
      {String(text)}
    </Tooltip>
  ) : null

// ── Filter dropdowns ─────────────────────────────────────────────────────────

interface ReferenceFilterProps {
  variables: any // eslint-disable-line @typescript-eslint/no-explicit-any
  setVariables: (v: any) => void // eslint-disable-line @typescript-eslint/no-explicit-any
  confirm: () => void
  clearFilters?: () => void
  placeholder: string
}

const ReferenceFilterDropdown = ({
  variables,
  setVariables,
  confirm,
  clearFilters,
  placeholder,
}: ReferenceFilterProps) => {
  const [value, setValue] = useState<string>(variables.search?.value || '')

  const apply = (v: string) => {
    setVariables({ ...variables, search: { value: v || null } })
    confirm()
  }

  const reset = () => {
    setValue('')
    setVariables({ ...variables, search: { value: null } })
    clearFilters?.()
    confirm()
  }

  return (
    <div style={{ padding: 8 }}>
      <Input.Search
        allowClear
        value={value}
        placeholder={placeholder}
        style={{ width: 280, marginBottom: 8, display: 'block' }}
        onChange={e => setValue(e.target.value)}
        onSearch={apply}
        onPressEnter={() => apply(value)}
      />
      <Space>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          size="small"
          onClick={() => apply(value)}
        >
          Rechercher
        </Button>
        <Button size="small" onClick={reset}>
          Réinitialiser
        </Button>
      </Space>
    </div>
  )
}

interface DateFilterProps {
  variables: any // eslint-disable-line @typescript-eslint/no-explicit-any
  setVariables: (v: any) => void // eslint-disable-line @typescript-eslint/no-explicit-any
  confirm: () => void
  clearFilters?: () => void
}

const DateFilterDropdown = ({
  variables,
  setVariables,
  confirm,
  clearFilters,
}: DateFilterProps) => {
  const currentRange = (): [Dayjs | null, Dayjs | null] => {
    const pd = variables.filters?.publication_date
    if (!pd || pd.length < 2) return [null, null]
    return [pd[0] ? dayjs(pd[0]) : null, pd[1] ? dayjs(pd[1]) : null]
  }

  const apply = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const from = dates?.[0]?.format('YYYY-MM-DD') ?? null
    const to = dates?.[1]?.format('YYYY-MM-DD') ?? null
    const { publication_date: _pd, ...rest } = variables.filters || {}

    if (from || to) {
      setVariables({
        ...variables,
        filters: { ...rest, publication_date: [from, to].filter(Boolean) },
      })
    } else {
      setVariables({ ...variables, filters: rest })
    }
    confirm()
  }

  const reset = () => {
    const { publication_date: _pd, ...rest } = variables.filters || {}
    setVariables({ ...variables, filters: rest })
    clearFilters?.()
    confirm()
  }

  return (
    <div style={{ padding: 8 }}>
      <RangePicker
        value={currentRange()}
        onChange={apply}
        format="DD/MM/YYYY"
        placeholder={['Date publi. de', 'à']}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Button size="small" onClick={reset}>
        Réinitialiser
      </Button>
    </div>
  )
}

// ── Column builders ──────────────────────────────────────────────────────────

const commonColumns = (
  variables: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  setVariables: (v: any) => void, // eslint-disable-line @typescript-eslint/no-explicit-any
  providerKey: string
): ColumnType<LegalNoticeIndexTableType>[] => [
  {
    title: 'Statut',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    ...getStatusColumnProps(variables, providerKey),
    render: (text: string) =>
      text ? <Tag color={STATUS_COLORS[text] ?? 'default'}>{text}</Tag> : null,
  },
  {
    title: 'Date de publication',
    dataIndex: 'publicationDate',
    key: 'publicationDate',
    width: 160,
    sorter: false,
    filtered: !!variables.filters?.publication_date,
    filterIcon: <CalendarOutlined />,
    filterDropdown: ({ confirm, clearFilters }) => (
      <DateFilterDropdown
        variables={variables}
        setVariables={setVariables}
        confirm={confirm}
        clearFilters={clearFilters}
      />
    ),
  },
]

const externalColumns = (
  variables: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  setVariables: (v: any) => void // eslint-disable-line @typescript-eslint/no-explicit-any
): ColumnType<LegalNoticeIndexTableType>[] => [
  {
    title: 'Référence ALP',
    dataIndex: 'reference',
    key: 'reference',
    width: 160,
    ellipsis: { showTitle: false },
    filtered: !!variables.search?.value,
    filterIcon: <SearchOutlined />,
    filterDropdown: ({ confirm, clearFilters }) => (
      <ReferenceFilterDropdown
        variables={variables}
        setVariables={setVariables}
        confirm={confirm}
        clearFilters={clearFilters}
        placeholder="Rechercher par référence ALP…"
      />
    ),
    render: renderTooltip,
  },
  {
    title: 'Référence client',
    dataIndex: 'customerReference',
    key: 'customerReference',
    width: 150,
    ellipsis: { showTitle: false },
    render: renderTooltip,
  },
  {
    title: 'Département',
    dataIndex: 'departmentCode',
    key: 'departmentCode',
    width: 110,
  },
  {
    title: 'Prix HT (€)',
    dataIndex: 'priceHt',
    key: 'priceHt',
    width: 100,
    render: (v: number | null) => (v != null ? v.toFixed(2) : null),
  },
  {
    title: 'Prix TTC (€)',
    dataIndex: 'priceTtc',
    key: 'priceTtc',
    width: 110,
    render: (v: number | null) => (v != null ? v.toFixed(2) : null),
  },
]

const internalColumns = (
  variables: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  setVariables: (v: any) => void // eslint-disable-line @typescript-eslint/no-explicit-any
): ColumnType<LegalNoticeIndexTableType>[] => [
  {
    title: 'ID Formalité',
    dataIndex: 'legalFormalityId',
    key: 'legalFormalityId',
    width: 110,
    render: (text: string | null, record: LegalNoticeIndexTableType) =>
      text ? (
        <Link
          to={`/contacts/${record.legalFormalityId}/legal_formalities/${record.legalFormalityId}`}
        >
          {text}
        </Link>
      ) : null,
  },
  {
    title: 'Société',
    dataIndex: 'societyName',
    key: 'societyName',
    width: 200,
    ellipsis: { showTitle: false },
    render: renderTooltip,
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: 200,
    ellipsis: { showTitle: false },
    render: renderTooltip,
  },
  {
    title: 'Réf. externe',
    dataIndex: 'externalRef',
    key: 'externalRef',
    width: 160,
    filtered: !!variables.search?.value,
    filterIcon: <SearchOutlined />,
    filterDropdown: ({ confirm, clearFilters }) => (
      <ReferenceFilterDropdown
        variables={variables}
        setVariables={setVariables}
        confirm={confirm}
        clearFilters={clearFilters}
        placeholder="Rechercher par réf. externe, ID, email, société…"
      />
    ),
    render: renderTooltip,
  },
  {
    title: 'ID externe',
    dataIndex: 'externalId',
    key: 'externalId',
    width: 120,
    render: renderTooltip,
  },
  {
    title: 'Créé le',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 140,
  },
  {
    title: 'Provider',
    dataIndex: 'provider',
    key: 'provider',
    width: 120,
    ...getProviderColumnProps(variables),
  },
]

export const columns = (
  variables: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  setVariables: (v: any) => void, // eslint-disable-line @typescript-eslint/no-explicit-any
  providerKey: string
): ColumnType<LegalNoticeIndexTableType>[] => {
  if (providerKey === 'internal') {
    return [
      ...internalColumns(variables, setVariables),
      ...commonColumns(variables, setVariables, providerKey),
    ]
  }
  return [
    ...commonColumns(variables, setVariables, providerKey),
    ...externalColumns(variables, setVariables),
  ]
}
